import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type Method,
} from "axios";
import { createApiService } from "sipli-fleet-core/api";
import { getFromLocal, saveToLocal, clearLocalStorage } from "./cache.service";
import { LOCAL_KEYS, HTTP_STATUS_CODES } from "./util.constant";
import { API_URL } from "./environment";
import Notification from "./libs/notification";
import { ServicesUrlEndpoints } from "src/services/servicesEndpointConstant";
import { getApiLanguage } from "src/services/commonLanguageHeader";
import { withDistributorBasename } from "src/util/distributorBranding.service";

type RequestConfig = AxiosRequestConfig & {
  skipErrorNotification?: boolean;
};

interface RefreshTokenPayload {
  access_token?: string;
  refresh_token?: string;
}

interface RefreshTokenResponse {
  response?: RefreshTokenPayload & {
    token?: RefreshTokenPayload;
  };
}

const apiService: AxiosInstance = axios.create({
  baseURL: API_URL(),
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

const getStoredAuthValue = (key: string) =>
  getFromLocal<string>(key, false, true);

apiService.interceptors.request.use(
  (config) => {
    if (config.headers) {
      config.headers["Accept-Language"] = getApiLanguage();
    }

    return config;
  },
  (error) => Promise.reject(error)
);

const extractRefreshTokens = (data: RefreshTokenResponse) => {
  const tokenData = data.response?.token ?? data.response;

  return {
    accessToken: tokenData?.access_token,
    refreshToken: tokenData?.refresh_token,
  };
};

const sharedApiService = createApiService({
  axiosInstance: apiService,
  getAccessToken: () => getStoredAuthValue(LOCAL_KEYS.TOKEN),
  getRefreshToken: () => getStoredAuthValue(LOCAL_KEYS.REFRESH_TOKEN),
  saveAuthTokens: ({ accessToken, refreshToken }) => {
    if (accessToken) {
      saveToLocal(LOCAL_KEYS.TOKEN, accessToken, false, true);
    }
    if (refreshToken) {
      saveToLocal(LOCAL_KEYS.REFRESH_TOKEN, refreshToken, false, true);
    }
  },
  clearAuth: clearLocalStorage,
  refreshUrl: ServicesUrlEndpoints.AUTH.REFRESH,
  extractRefreshTokens,
  onUnauthorized: () => {
    window.location.href = withDistributorBasename("/login");
  },
  notifyError: (error: AxiosError<{ message?: string }>) => {
    if (error.response?.data?.message) {
      Notification.error({
        message: "Error",
        description: error.response.data.message,
      });
    }
  },
  invalidCredentialsErrorCode: 0,
  httpStatusCodes: HTTP_STATUS_CODES,
});

const requestWithResponse = <T = unknown>(
  method: Method,
  url: string,
  data: unknown = {},
  config: RequestConfig = {}
) =>
  sharedApiService.request(
    method,
    url,
    data,
    config,
    false,
    true
  ) as Promise<AxiosResponse<T>>;

export const api = {
  get: <T = unknown>(url: string, config?: RequestConfig) =>
    requestWithResponse<T>("get", url, {}, config),
  post: <T = unknown>(url: string, data?: unknown, config?: RequestConfig) =>
    requestWithResponse<T>("post", url, data, config),
  put: <T = unknown>(url: string, data?: unknown, config?: RequestConfig) =>
    requestWithResponse<T>("put", url, data, config),
  patch: <T = unknown>(url: string, data?: unknown, config?: RequestConfig) =>
    requestWithResponse<T>("patch", url, data, config),
  delete: <T = unknown>(url: string, config?: RequestConfig) =>
    requestWithResponse<T>("delete", url, {}, config),
};

class ApiService {
  static get<T = unknown>(url: string, config?: RequestConfig): Promise<AxiosResponse<T>> {
    return api.get<T>(url, config);
  }

  static post<T = unknown>(url: string, data?: unknown, config?: RequestConfig): Promise<AxiosResponse<T>> {
    return api.post<T>(url, data, config);
  }

  static put<T = unknown>(url: string, data?: unknown, config?: RequestConfig): Promise<AxiosResponse<T>> {
    return api.put<T>(url, data, config);
  }

  static patch<T = unknown>(url: string, data?: unknown, config?: RequestConfig): Promise<AxiosResponse<T>> {
    return api.patch<T>(url, data, config);
  }

  static delete<T = unknown>(url: string, config?: RequestConfig): Promise<AxiosResponse<T>> {
    return api.delete<T>(url, config);
  }
}

export default ApiService;
